import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { LogOut, ShieldCheck, Wallet, Phone, Layers, Pencil, MapPin, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { PILLARS } from "@/lib/brand";
import { ProfileEditForm } from "@/components/ProfileEditForm";
import { AthleteLinkCard } from "@/components/AthleteLinkCard";
import { PrivacyDataCard } from "@/components/PrivacyDataCard";
import { ShareIdCard } from "@/components/ShareIdCard";
import { ProfilePhoto, photoSrc } from "@/components/ProfilePhoto";
import { MyInvitesCard } from "@/components/MyInvitesCard";

export const UserProfileModal = ({ open, onOpenChange }) => {
  const { user, logout } = useAuth();
  const [editing, setEditing] = useState(false);
  if (!user) return null;
  const src = photoSrc(user);

  const doLogout = async () => {
    await logout();
    toast.success("Signed out of One Talanta ID");
    onOpenChange(false);
  };

  const hasAccess = (p) => p.state === "active" || p.state === "connected" || user.pillars?.includes(p.id);

  return (
    <Dialog open={open} onOpenChange={(o) => { setEditing(false); onOpenChange(o); }}>
      <DialogContent data-testid="user-profile-modal" className="sm:max-w-md p-0 overflow-hidden border-0 bg-white max-h-[92vh] overflow-y-auto">
        <DialogTitle className="sr-only">Your One Talanta ID</DialogTitle>
        <DialogDescription className="sr-only">Your digital ID card, profile, wallet and pillar access.</DialogDescription>
        <div className="kenya-stripe h-1.5" />
        <div className="relative px-6 pt-7 pb-6 text-white" style={{ background: "linear-gradient(135deg, #083323 0%, #0E4733 100%)" }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-talanta-gold text-xs font-semibold uppercase tracking-widest">
              <ShieldCheck className="h-4 w-4" /> Digital ID
            </div>
            <span className="text-[10px] uppercase tracking-widest text-white/50">
              {user.role === "owner" ? "Owner · " : ""}Talanta Hela
            </span>
          </div>

          <div className="mt-5 flex items-center gap-4">
            <ProfilePhoto size={84} />
            <div className="rounded-xl bg-white p-1.5 shrink-0" data-testid="user-qr-code">
              <QRCodeSVG value={JSON.stringify({ id: user.talanta_id, phone: user.phone })} size={60} fgColor="#083323" />
            </div>
            <div className="min-w-0">
              <div data-testid="user-display-name" className="font-display font-bold text-xl truncate">{user.name || "Talanta Member"}</div>
              <div data-testid="user-id-badge" className="mt-1 inline-flex items-center rounded-md bg-white/10 px-2 py-1 font-mono text-sm text-talanta-gold">
                {user.talanta_id}
              </div>
              <div className="mt-1.5 flex items-center gap-1.5 text-sm text-white/70"><Phone className="h-3.5 w-3.5" /> {user.phone}</div>
              {(user.county || user.email) && (
                <div data-testid="user-meta" className="mt-1 flex flex-wrap gap-x-3 text-xs text-white/60">
                  {user.county && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {user.county}</span>}
                  {user.email && <span className="flex items-center gap-1 truncate"><Mail className="h-3 w-3" /> {user.email}</span>}
                </div>
              )}
            </div>
          </div>
          {!src && (
            <p className="mt-3 text-xs text-white/60" data-testid="photo-hint">
              Take a live photo so your ID card is recognisable — tap the camera.
            </p>
          )}
        </div>

        <div className="px-6 py-5 space-y-4">
          {editing ? (
            <ProfileEditForm onDone={() => setEditing(false)} />
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="profile-edit-btn"
                onClick={() => setEditing(true)}
                className="w-full rounded-xl border border-talanta-green/15 py-2.5 text-sm font-semibold text-talanta-green hover:bg-talanta-cream flex items-center justify-center gap-2"
              >
                <Pencil className="h-4 w-4" /> Edit profile
              </button>
              <ShareIdCard />
            </div>
          )}

          <MyInvitesCard />

          <AthleteLinkCard />

          <PrivacyDataCard onClose={() => onOpenChange(false)} />

          <div className="rounded-xl bg-talanta-cream border border-talanta-green/10 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-talanta-gold/15 flex items-center justify-center">
                <Wallet className="h-5 w-5 text-talanta-terracotta" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-talanta-charcoal/50">Talanta Wallet</div>
                <div className="font-display font-bold text-talanta-green">KES 0.00</div>
              </div>
            </div>
            <span className="text-xs font-semibold text-amber-600 bg-amber-500/10 px-2.5 py-1 rounded-full">Soon</span>
          </div>

          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-talanta-charcoal/50">
              <Layers className="h-3.5 w-3.5" /> Pillar access
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {PILLARS.map((p) => (
                <span
                  key={p.id}
                  className={`text-xs font-semibold rounded-full px-3 py-1 border ${
                    hasAccess(p)
                      ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30"
                      : "bg-talanta-cream text-talanta-charcoal/50 border-talanta-green/10"
                  }`}
                >
                  {p.short}
                </span>
              ))}
            </div>
          </div>

          <button
            data-testid="user-logout-btn"
            onClick={doLogout}
            className="w-full rounded-xl border border-talanta-red/30 text-talanta-red hover:bg-talanta-red hover:text-white py-3 font-semibold flex items-center justify-center gap-2 transition-colors"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
